import { Phone, CalendarCheck, ShieldCheck, Sparkles, MapPin, Instagram, Facebook } from "lucide-react";
import { Button } from "@/components/ui/button";
import { BUSINESS } from "@/lib/api";

export default function Hero({ onOpenBooking }) {
  return (
    <section
      data-testid="hero-section"
      className="relative overflow-hidden bg-[#0B3A5B] text-white"
    >
      {/* Authentic photo background with dark blue overlay */}
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage:
            "url('https://customer-assets.emergentagent.com/job_auto-detailing-pro-16/artifacts/5z9mkx1f_j-z-aKwjqJjpUBQ-unsplash.jpg')",
        }}
      />
      {/* Dark blue overlay 80% to keep text legible */}
      <div className="absolute inset-0 bg-[#0B3A5B]/85" />
      <div className="absolute inset-0 bg-gradient-to-br from-[#0B3A5B]/40 via-transparent to-[#072337]/60" />
      <div className="absolute -top-32 -right-32 w-[480px] h-[480px] rounded-full bg-[#1E88C8]/20 blur-3xl" />
      <div className="absolute -bottom-32 -left-32 w-[420px] h-[420px] rounded-full bg-[#1E88C8]/10 blur-3xl" />
      <div
        className="absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage:
            "radial-gradient(circle at 1px 1px, rgba(255,255,255,0.8) 1px, transparent 0)",
          backgroundSize: "28px 28px",
        }}
      />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 border border-white/15 backdrop-blur text-xs uppercase tracking-wider text-white/90 mb-6">
            <MapPin className="h-3.5 w-3.5 text-[#1E88C8]" />
            {BUSINESS.region} · Service à domicile
          </div>

          <h1
            data-testid="hero-title"
            className="font-display text-[2.5rem] leading-[1.05] sm:text-5xl lg:text-6xl text-white tracking-tight"
          >
            Retrouvez un intérieur
            <span className="block text-[#1E88C8]">comme neuf.</span>
          </h1>

          <p className="mt-6 text-base sm:text-lg text-white/85 max-w-2xl leading-relaxed">
            Nettoyage intérieur automobile à domicile dans les Côtes-d&apos;Armor.
            Aspiration complète, shampoing des sièges, désinfection vapeur
            et finitions soignées.
          </p>

          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <a
              href={`tel:${BUSINESS.phone}`}
              data-testid="hero-call-button"
              className="btn-press inline-flex items-center justify-center gap-2 h-14 px-7 rounded-full bg-white text-[#0B3A5B] font-semibold text-base hover:bg-white/95 hover:scale-[1.02] transition shadow-lg shadow-[#0B3A5B]/30"
            >
              <Phone className="h-5 w-5" />
              Appeler maintenant
            </a>
            <Button
              data-testid="hero-booking-button"
              onClick={onOpenBooking}
              className="btn-press h-14 px-7 rounded-full bg-[#1E88C8] hover:bg-[#1670a8] hover:scale-[1.02] text-white font-semibold text-base shadow-lg shadow-[#1E88C8]/30"
            >
              <CalendarCheck className="h-5 w-5 mr-2" />
              Prendre rendez-vous
            </Button>
          </div>

          {/* Social reassurance links under main CTAs */}
          <div className="mt-5 flex flex-col sm:flex-row gap-3 sm:gap-5">
            <a
              href={BUSINESS.instagram}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-instagram-link"
              className="inline-flex items-center gap-2 text-sm font-semibold text-white/90 hover:text-white transition group"
            >
              <span className="h-9 w-9 rounded-full bg-gradient-to-br from-[#feda75] via-[#d62976] to-[#4f5bd5] flex items-center justify-center transition-transform group-hover:scale-110 ring-1 ring-white/20">
                <Instagram className="h-4 w-4" />
              </span>
              <span className="underline-offset-4 group-hover:underline">
                Voir les réalisations sur Instagram
              </span>
            </a>
            <a
              href={BUSINESS.facebook}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-facebook-link"
              className="inline-flex items-center gap-2 text-sm font-semibold text-white/90 hover:text-white transition group"
            >
              <span className="h-9 w-9 rounded-full bg-[#1877F2] flex items-center justify-center transition-transform group-hover:scale-110 ring-1 ring-white/20">
                <Facebook className="h-4 w-4 fill-current" />
              </span>
              <span className="underline-offset-4 group-hover:underline">
                Suivre sur Facebook
              </span>
            </a>
            <a
              href="https://g.page/r/CYnLwJG8BuS0EAE/review"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-google-review-link"
              className="inline-flex items-center gap-2 text-sm font-semibold text-white/90 hover:text-white transition group"
            >
              <span className="h-9 w-9 rounded-full bg-white flex items-center justify-center transition-transform group-hover:scale-110 ring-1 ring-white/20">
                <svg className="h-4 w-4" viewBox="0 0 48 48" aria-hidden>
                  <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.1 7.9 3l5.7-5.7C34.5 5.9 29.6 4 24 4 13 4 4 13 4 24s9 20 20 20 20-9 20-20c0-1.3-.1-2.4-.4-3.5z" />
                  <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16.1 19 13 24 13c3.1 0 5.8 1.1 7.9 3l5.7-5.7C34.5 5.9 29.6 4 24 4 16.3 4 9.6 8.3 6.3 14.7z" />
                  <path fill="#4CAF50" d="M24 44c5.5 0 10.3-1.9 13.9-5l-6.4-5.4c-2 1.5-4.6 2.4-7.5 2.4-5.3 0-9.7-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z" />
                  <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.7 2-2 3.7-3.7 5l6.4 5.4C42.6 35.4 44 30.1 44 24c0-1.3-.1-2.4-.4-3.5z" />
                </svg>
              </span>
              <span className="underline-offset-4 group-hover:underline inline-flex items-center gap-1">
                Laisser un avis Google
                <span className="text-[#FBBC04]" aria-hidden>★</span>
              </span>
            </a>
          </div>

          <div className="mt-10 flex flex-wrap gap-x-8 gap-y-4 text-sm text-white/85">
            <div className="flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-[#1E88C8]" />
              Produits professionnels
            </div>
            <div className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-[#1E88C8]" />
              Désinfection vapeur
            </div>
            <div className="flex items-center gap-2">
              <MapPin className="h-4 w-4 text-[#1E88C8]" />
              Service à domicile
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
